import axios from '../utils/axiosConfig';
import communityAPI from './community.api.js';

const marketplaceAPI = {
  // User tier and stats
  getUserTier: async () => {
    const response = await axios.get('/marketplace/user/tier');
    return response.data;
  },

  getUserStats: async () => {
    const response = await axios.get('/marketplace/user/stats');
    return response.data;
  },

  // Business expiry and info
  getBusinessExpiry: async () => {
    const response = await axios.get('/marketplace/business/expiry');
    return response.data;
  },

  getUserBusiness: async () => {
    const response = await axios.get('/marketplace/user/business');
    return response.data;
  },

  // Categories
  getCategories: async () => {
    const response = await axios.get('/marketplace/categories');
    return response.data;
  },

  // Businesses
  createBusiness: async (data) => {
    const response = await axios.post('/marketplace/businesses', data);
    return response.data;
  },

  searchBusinesses: async (params = {}) => {
    const response = await axios.get('/marketplace/businesses', { params });
    return response.data;
  },

  getFeaturedBusinesses: async () => {
    const response = await axios.get('/marketplace/businesses/featured');
    return response.data;
  },

  getBusinessDetails: async (businessId) => {
    const response = await axios.get(`/marketplace/businesses/${businessId}`);
    return response.data;
  },

  // Product and service limit checks
  checkProductLimit: async (businessId) => {
    const response = await axios.get(`/marketplace/businesses/${businessId}/product-limit`);
    return response.data;
  },

  checkServiceLimit: async (businessId) => {
    const response = await axios.get(`/marketplace/businesses/${businessId}/service-limit`);
    return response.data;
  },

  // Messaging - Updated to use community messaging internally
  sendMessage: async (data) => {
    // Forward to community API
    const response = await communityAPI.sendMessage(data);
    return response;
  },

  getConversations: async (params = {}) => {
    // Add business type filter
    const response = await communityAPI.getConversations({ ...params, type: 'business' });
    return response;
  },

  getMessages: async (conversationId, params = {}) => {
    const response = await axios.get(`/marketplace/conversations/${conversationId}/messages`, { params });
    return response.data;
  },

  // Reviews
  createReview: async (data) => {
    const response = await axios.post('/marketplace/reviews', data);
    return response.data;
  },

  getReviews: async (params = {}) => {
    const response = await axios.get('/marketplace/reviews', { params });
    return response.data;
  },

  addSellerResponse: async (reviewId, response) => {
    const result = await axios.post(`/marketplace/reviews/${reviewId}/response`, { response });
    return result.data;
  },

  // Business Profile
  getBusinessProfile: async (businessId) => {
    const response = await axios.get(`/marketplace/businesses/${businessId}/profile`);
    return response.data;
  },

  getBusinessStats: async (businessId) => {
    const response = await axios.get(`/marketplace/businesses/${businessId}/stats`);
    return response.data;
  },

  // Business Management
  updateBusiness: async (businessId, data) => {
    const response = await axios.put(`/marketplace/businesses/${businessId}`, data);
    return response.data;
  },

  deleteBusiness: async (businessId) => {
    const response = await axios.delete(`/marketplace/businesses/${businessId}`);
    return response.data;
  },

  updateBusinessStatus: async (businessId, status) => {
    const response = await axios.patch(`/marketplace/businesses/${businessId}/status`, { status });
    return response.data;
  },

  // Products
  createProduct: async (businessId, data) => {
    const response = await axios.post(`/marketplace/businesses/${businessId}/products`, data);
    return response.data;
  },

  getProducts: async (businessId, params = {}) => {
    const response = await axios.get(`/marketplace/businesses/${businessId}/products`, { params });
    return response.data;
  },

  updateProduct: async (productId, data) => {
    const response = await axios.put(`/marketplace/products/${productId}`, data);
    return response.data;
  },

  deleteProduct: async (productId) => {
    const response = await axios.delete(`/marketplace/products/${productId}`);
    return response.data;
  },

  // Services
  createService: async (businessId, data) => {
    const response = await axios.post(`/marketplace/businesses/${businessId}/services`, data);
    return response.data;
  },

  getServices: async (businessId, params = {}) => {
    const response = await axios.get(`/marketplace/businesses/${businessId}/services`, { params });
    return response.data;
  },

  updateService: async (serviceId, data) => {
    const response = await axios.put(`/marketplace/services/${serviceId}`, data);
    return response.data;
  },

  deleteService: async (serviceId) => {
    const response = await axios.delete(`/marketplace/services/${serviceId}`);
    return response.data;
  },

  // =================== ORDER MANAGEMENT ===================
  createOrder: async (data) => {
    const response = await axios.post('/marketplace/orders', data);
    return response.data;
  },

  getUserOrders: async (params = {}) => {
    const response = await axios.get('/marketplace/orders', { params });
    return response.data;
  },

  getOrderDetails: async (orderId) => {
    const response = await axios.get(`/marketplace/orders/${orderId}`);
    return response.data;
  },

  updateOrderStatus: async (orderId, status) => {
    const response = await axios.patch(`/marketplace/orders/${orderId}/status`, { status });
    return response.data;
  },

  canReviewOrder: async (orderId) => {
    const response = await axios.get(`/marketplace/orders/${orderId}/can-review`);
    return response.data;
  },

  // =================== INVENTORY MANAGEMENT ===================
  updateInventory: async (productId, quantity, operation = 'set') => {
    const response = await axios.patch(`/marketplace/products/${productId}/inventory`, {
      quantity,
      operation
    });
    return response.data;
  },

  getInventoryAlerts: async (params = {}) => {
    const response = await axios.get('/marketplace/inventory/alerts', { params });
    return response.data;
  },

  // Category endpoints
  getBusinessCategories: async () => {
    const response = await axios.get('/marketplace/categories/business');
    return response.data;
  },

  getProductServiceCategories: async () => {
    const response = await axios.get('/marketplace/categories/product-service');
    return response.data;
  },

  getSubcategories: async (parentCategory) => {
    const response = await axios.get(`/marketplace/subcategories/${parentCategory}`);
    return response.data;
  },

  getAllowedProductCategories: async (businessId) => {
    const response = await axios.get(`/marketplace/businesses/${businessId}/allowed-categories`);
    return response.data;
  },

  // =================== BUSINESS ORDERS (SELLER VIEW) ===================
  getBusinessOrders: async (businessId, params = {}) => {
    const response = await axios.get(`/marketplace/businesses/${businessId}/orders`, { params });
    return response.data;
  },

  checkCanCreateBusiness: async () => {
    const response = await axios.get('/marketplace/business/can-create');
    return response.data;
  },

  // =================== ORDER STATS ===================
  getOrderStats: async (params = {}) => {
    const response = await axios.get('/marketplace/orders/stats', { params });
    return response.data;
  },

  // Favorites
  toggleFavorite: async (businessId) => {
    const response = await axios.post(`/marketplace/businesses/${businessId}/favorite`);
    return response.data;
  },

  getFavorites: async (params = {}) => {
    const response = await axios.get('/marketplace/favorites', { params });
    return response.data;
  },

  checkFavorite: async (businessId) => {
    const response = await axios.get(`/marketplace/businesses/${businessId}/favorite/check`);
    return response.data;
  },

  // Notifications
  getMarketplaceNotifications: async (params = {}) => {
    const response = await axios.get('/marketplace/notifications', { params });
    return response.data;
  },

  markNotificationsAsRead: async (notificationIds = []) => {
    const response = await axios.post('/marketplace/notifications/read', { notificationIds });
    return response.data;
  },

  // Upload endpoints
  uploadImages: async (files) => {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`images[${index}]`, file);
    });
    const response = await axios.post('/marketplace/upload/images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await axios.post('/marketplace/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }
};

export default marketplaceAPI;